<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <!-- Header Section -->
    <div class="mb-8">
      <h1 class="text-2xl font-semibold text-gray-900">Follow-ups</h1>
      <p class="mt-1 text-sm text-gray-600">
        Smart recommendations for follow-up emails
      </p>
    </div>

    <!-- Controls Section -->
    <div class="mb-6">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <!-- Search and Filter -->
        <div class="flex flex-col sm:flex-row gap-3 flex-1">
          <!-- Search Input -->
          <div class="relative flex-1 max-w-md">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
              </svg>
            </div>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name, email, or subject..."
              class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              @input="onSearchInput"
            />
          </div>
          
          <!-- Institution Filter -->
          <select
            v-model="selectedInstitutionId"
            class="block w-full sm:w-48 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            @change="fetchFollowUps"
          >
            <option value="">All Institutions</option>
            <option v-for="inst in institutions" :key="inst.id" :value="inst.id">
              {{ inst.name }}
            </option>
          </select>
        </div>

        <!-- Action buttons -->
        <div class="flex gap-2 flex-shrink-0">
          <!-- Manual Follow-up Button -->
          <button
            @click="openManualFollowUpModal"
            class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <svg class="h-4 w-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Follow-up
          </button>

          <!-- Refresh Button -->
          <button
            @click="refreshRecommendations"
            :disabled="loading"
            class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <svg class="h-4 w-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span v-if="loading">Loading...</span>
            <span v-else">Refresh</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Statistics Dashboard -->
    <div v-if="statistics && !loading" class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
                <span class="text-sm font-medium text-red-600">H</span>
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">High Priority</dt>
                <dd class="text-lg font-medium text-gray-900">{{ statistics.urgency_breakdown.high || 0 }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                <span class="text-sm font-medium text-yellow-600">M</span>
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">Medium Priority</dt>
                <dd class="text-lg font-medium text-gray-900">{{ statistics.urgency_breakdown.medium || 0 }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                <span class="text-sm font-medium text-blue-600">L</span>
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">Low Priority</dt>
                <dd class="text-lg font-medium text-gray-900">{{ statistics.urgency_breakdown.low || 0 }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                <span class="text-sm font-medium text-green-600">T</span>
              </div>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">Total Recommendations</dt>
                <dd class="text-lg font-medium text-gray-900">{{ statistics.total_recommendations }}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected Items Actions -->
    <div v-if="selectedItems.length > 0" class="mt-6 bg-white shadow rounded-lg p-6">
      <div class="flex items-center justify-between mb-4">
        <span class="text-sm text-gray-700">
          {{ selectedItems.length }} items selected
        </span>
        <div class="flex space-x-3">
          <button
            @click="handleGenerateSelectedFollowUps"
            :disabled="actionInProgress"
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <svg v-if="actionInProgress" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-if="actionInProgress">Generating...</span>
            <span v-else>Generate Selected</span>
          </button>
          <button
            @click="handleSkipSelectedFollowUps"
            :disabled="actionInProgress"
            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            Skip Selected
          </button>
          <button
            @click="clearSelection"
            class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear Selection
          </button>
        </div>
      </div>
    </div>


    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center py-10">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      <p class="ml-2 text-gray-600">Loading follow-ups...</p>
    </div>

    <!-- Error message -->
    <div v-else-if="error" class="rounded-md bg-red-50 p-4 mt-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">{{ error }}</h3>
        </div>
      </div>
    </div>

    <!-- Success message -->
    <div v-if="successMessage" class="rounded-md bg-green-50 p-4 mt-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-green-800">{{ successMessage }}</h3>
        </div>
      </div>
    </div>

    <!-- Follow-ups List -->
    <div v-if="!loading && !error" class="mt-8 flex flex-col">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 lg:pl-8 w-16">
                  <input
                    type="checkbox"
                    :checked="selectedItems.length === recommendations.length && recommendations.length > 0"
                    :indeterminate="selectedItems.length > 0 && selectedItems.length < recommendations.length"
                    @change="toggleAllItems"
                    class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <th scope="col" class="py-3.5 px-3 text-center text-sm font-semibold text-gray-900 w-20">
                  Priority
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-4/12">
                  Subject
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-2/12">
                  Recipient
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-2/12">
                  Status
                </th>
                <th scope="col" class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900 w-16">
                  Days
                </th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-3/12">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              <tr v-for="(rec, index) in recommendations" :key="rec.email_id" class="hover:bg-gray-50" :class="{
                'bg-red-50': rec.urgency_level === 'high',
                'bg-yellow-50': rec.urgency_level === 'medium',
                'bg-blue-50': rec.urgency_level === 'low',
                'bg-indigo-50': selectedItems.includes(rec.email_id)
              }">
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6 lg:pl-8">
                  <div 
                    @click="(event) => handleRowSelection(event, index)" 
                    @contextmenu.prevent
                    class="cursor-pointer flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 select-none"
                    style="user-select: none; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none;"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedItems.includes(rec.email_id)"
                      readonly
                      class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 pointer-events-none"
                    />
                  </div>
                </td>
                <td class="whitespace-nowrap py-4 px-3 text-sm">
                  <div class="text-center">
                    <div class="mb-1">
                      <span :class="[
                        rec.urgency_level === 'high' ? 'bg-red-100 text-red-800' : 
                        rec.urgency_level === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                        rec.urgency_level === 'low' ? 'bg-blue-100 text-blue-800' : 
                        'bg-gray-100 text-gray-800',
                        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold leading-4'
                      ]">
                        {{ rec.priority_score }}
                      </span>
                    </div>
                    <div :class="[
                      rec.urgency_level === 'high' ? 'text-red-600' : 
                      rec.urgency_level === 'medium' ? 'text-yellow-600' : 
                      rec.urgency_level === 'low' ? 'text-blue-600' : 
                      'text-gray-600',
                      'text-xs uppercase font-medium'
                    ]">
                      {{ rec.urgency_level }}
                    </div>
                  </div>
                </td>
                <td class="whitespace-normal py-4 px-3 text-sm">
                  <div class="font-medium text-gray-900">{{ rec.subject }}</div>
                  <div class="text-xs text-gray-500 mt-1">Follow-up #{{ rec.follow_up_count + 1 }}</div>
                </td>
                <td class="whitespace-normal px-3 py-4 text-sm text-gray-500">
                  <div>{{ rec.recipient_name }}</div>
                  <div class="text-xs text-gray-400">{{ rec.recipient_email }}</div>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm">
                  <div class="text-sm text-gray-900">{{ rec.status_description }}</div>
                  <div class="text-xs text-gray-500">{{ rec.recommended_action }}</div>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <div class="text-center">
                    <div class="font-bold text-lg text-gray-900">{{ rec.business_days_since_sent }}</div>
                    <div class="text-xs text-gray-400">days</div>
                  </div>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <div class="flex space-x-2">
                    <button 
                      @click="handleGenerateFollowUp(null, rec.email_id)"
                      :disabled="actionInProgress || generatingEmailIds.has(rec.email_id)"
                      class="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <svg v-if="generatingEmailIds.has(rec.email_id)" class="animate-spin -ml-1 mr-1.5 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span v-if="generatingEmailIds.has(rec.email_id)">Generating...</span>
                      <span v-else>Generate Follow-up</span>
                    </button>
                    <button 
                      @click="handleSkipFollowUp(rec.email_id)"
                      :disabled="actionInProgress"
                      class="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      Skip
                    </button>
                    <button 
                      @click="showRescheduleModal(rec)"
                      :disabled="actionInProgress"
                      class="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      Reschedule
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="recommendations.length === 0">
                <td colspan="7" class="px-3 py-4 text-sm text-gray-500 text-center">
                  No recommendations found
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Pagination -->
      <div v-if="paginationInfo.total_pages > 1" class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage <= 1"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage >= paginationInfo.total_pages"
            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p class="text-sm text-gray-700">
              Showing
              <span class="font-medium">{{ ((currentPage - 1) * paginationInfo.page_size) + 1 }}</span>
              to
              <span class="font-medium">{{ Math.min(currentPage * paginationInfo.page_size, paginationInfo.total_items) }}</span>
              of
              <span class="font-medium">{{ paginationInfo.total_items }}</span>
              results
            </p>
          </div>
          <div>
            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage <= 1"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              
              <template v-for="page in getPaginationPages()" :key="page">
                <button
                  v-if="page !== '...'"
                  @click="goToPage(page)"
                  :class="[
                    page === currentPage 
                      ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' 
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                  ]"
                >
                  {{ page }}
                </button>
                <span v-else class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  ...
                </span>
              </template>
              
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage >= paginationInfo.total_pages"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Reschedule Modal -->
    <div v-if="showReschedule" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Reschedule Follow-up</h3>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
          <input 
            type="date" 
            v-model="rescheduledDate" 
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>
        <div class="flex justify-end space-x-3">
          <button 
            @click="showReschedule = false"
            class="px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            @click="handleRescheduleFollowUp"
            :disabled="actionInProgress || !rescheduledDate"
            class="px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            Reschedule
          </button>
        </div>
      </div>
    </div>

    <!-- Manual Follow-up Modal -->
    <div v-if="showManualFollowUpModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">Create Manual Follow-up</h3>
          <div v-if="selectedItems.length > 0" class="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
            {{ selectedItems.length }} selected recipients
          </div>
        </div>
        
        <div class="space-y-4">
          <!-- Recipients Section -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">Recipients</label>
              <div v-if="selectedItems.length > 0" class="text-xs text-gray-500">
                Pre-filled from selected items
              </div>
            </div>
            <div class="space-y-2">
              <div v-for="(recipient, index) in manualFollowUp.recipients" :key="index" class="flex gap-2">
                <input 
                  v-model="recipient.email" 
                  type="email" 
                  placeholder="Email address"
                  class="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                <input 
                  v-model="recipient.name" 
                  type="text" 
                  placeholder="Name (optional)"
                  class="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                <button 
                  @click="removeRecipient(index)"
                  class="px-3 py-2 text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
              <button 
                @click="addRecipient"
                class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Recipient
              </button>
            </div>
          </div>

          <!-- Institution Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Institution (optional)</label>
            <select 
              v-model="manualFollowUp.institution_id"
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select Institution</option>
              <option v-for="inst in institutions" :key="inst.id" :value="inst.id">
                {{ inst.name }}
              </option>
            </select>
          </div>

          <!-- Subject -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input 
              v-model="manualFollowUp.subject" 
              type="text" 
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Follow-up email subject"
            />
          </div>

          <!-- Body -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email Content</label>
            <textarea 
              v-model="manualFollowUp.body" 
              rows="8"
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Write your follow-up email content here..."
            ></textarea>
          </div>

          <!-- CC Recipients -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">CC Recipients (optional)</label>
            <input 
              v-model="manualFollowUp.cc_recipients_text" 
              type="text" 
              placeholder="email1@domain.com, email2@domain.com"
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
            <p class="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
          </div>
        </div>

        <div class="flex justify-end space-x-3 mt-6">
          <button 
            @click="cancelManualFollowUp"
            class="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            @click="handleCreateManualFollowUp"
            :disabled="actionInProgress || !canCreateManualFollowUp"
            class="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
          >
            <span v-if="actionInProgress">Creating...</span>
            <span v-else>Create Follow-up</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Too Recent Email Confirmation Dialog -->
    <div v-if="showTooRecentDialog && tooRecentError" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div class="flex items-center mb-4">
          <div class="flex-shrink-0">
            <svg class="h-8 w-8 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-lg font-medium text-gray-900">Email Sent Too Recently</h3>
          </div>
        </div>

        <div class="mb-6">
          <p class="text-sm text-gray-600 mb-3">
            A follow-up email was sent only <strong>{{ tooRecentError.days_since_last }} day(s) ago</strong>. 
            To avoid overwhelming recipients, we recommend waiting at least 3 days between follow-ups.
          </p>
          <p class="text-xs text-gray-500">
            Last email sent: {{ new Date(tooRecentError.last_sent_date).toLocaleString() }}
          </p>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            @click="cancelForceGeneration"
            class="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            @click="handleForceGeneration"
            :disabled="actionInProgress"
            class="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
          >
            <span v-if="actionInProgress">Generating...</span>
            <span v-else>Send Anyway</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Overlay for bulk operations -->
    <div v-if="actionInProgress && generatingEmailIds.size === 0" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <div class="flex items-center">
          <svg class="animate-spin -ml-1 mr-3 h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <div>
            <p class="text-lg font-medium text-gray-900">Processing...</p>
            <p class="text-sm text-gray-500">Please wait while we process your request</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import apiClient from '@/api/client'
import { INSTITUTIONS } from '@/api/endpoints'
import { useFollowUps } from '@/composables/useFollowUps'
import { emailEvents } from '@/utils/eventBus'

// Get the follow-ups composable
const { 
  followUps, 
  recommendations,
  statistics,
  paginationInfo,
  loading, 
  error, 
  actionInProgress,
  fetchFollowUps: fetchFollowUpsData,
  fetchRecommendations,
  generateFollowUp,
  skipFollowUp,
  rescheduleFollowUp,
  processBulkRecommendations,
  createManualFollowUp
} = useFollowUps()

const institutions = ref<any[]>([])
const selectedInstitutionId = ref('')
const searchQuery = ref('')
const searchTimeout = ref<NodeJS.Timeout | null>(null)
const successMessage = ref('')
const showReschedule = ref(false)
const rescheduledDate = ref('')
const selectedFollowUp = ref<any>(null)

// Confirmation dialog for too-recent emails
const showTooRecentDialog = ref(false)
const tooRecentError = ref<any>(null)

// Loading state for individual follow-up generation
const generatingEmailIds = ref<Set<string>>(new Set())

// Manual follow-up variables
const showManualFollowUpModal = ref(false)
const manualFollowUp = ref({
  recipients: [{ email: '', name: '' }],
  subject: '',
  body: '',
  institution_id: '',
  cc_recipients_text: ''
})

// Selection variables
const selectedItems = ref<string[]>([])
const lastSelectedIndex = ref<number | null>(null)


// Pagination variables
const currentPage = ref(1)
const currentFilters = ref<any>({})

// Manual follow-up computed properties
const canCreateManualFollowUp = computed(() => {
  return manualFollowUp.value.recipients.some(r => r.email.trim()) && 
         manualFollowUp.value.subject.trim() && 
         manualFollowUp.value.body.trim()
})

function formatDate(date: string | null): string {
  if (!date) return 'Not scheduled'
  return new Date(date).toLocaleDateString()
}

// Search input handler with debouncing
function onSearchInput() {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
  
  searchTimeout.value = setTimeout(() => {
    currentPage.value = 1 // Reset to first page when searching
    fetchFollowUps()
  }, 500) // Wait 500ms after user stops typing
}

async function fetchFollowUps(page: number = 1) {
  try {
    const filters: any = {
      page: page,
      page_size: 100
    }
    if (selectedInstitutionId.value) {
      filters.institution_id = selectedInstitutionId.value
    }
    if (searchQuery.value && searchQuery.value.trim()) {
      filters.search = searchQuery.value.trim()
    }
    currentFilters.value = filters
    currentPage.value = page
    await fetchRecommendations(filters)
  } catch (error) {
    console.error('Error in fetchFollowUps:', error)
  }
}

async function refreshRecommendations() {
  await fetchFollowUps(currentPage.value)
}

async function goToPage(page: number) {
  if (page >= 1 && page <= paginationInfo.value.total_pages) {
    await fetchFollowUps(page)
  }
}

function getPaginationPages() {
  const current = currentPage.value
  const total = paginationInfo.value.total_pages
  const pages: (number | string)[] = []
  
  if (total <= 7) {
    // Show all pages if 7 or fewer
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    // Always show first page
    pages.push(1)
    
    if (current <= 4) {
      // Current page is near the beginning
      for (let i = 2; i <= 5; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    } else if (current >= total - 3) {
      // Current page is near the end
      pages.push('...')
      for (let i = total - 4; i <= total; i++) {
        pages.push(i)
      }
    } else {
      // Current page is in the middle
      pages.push('...')
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i)
      }
      pages.push('...')
      pages.push(total)
    }
  }
  
  return pages
}

async function loadInstitutions() {
  try {
    const response = await apiClient.get(INSTITUTIONS.LIST)
    institutions.value = response.data.items
  } catch (error) {
    console.error('Error loading institutions:', error)
  }
}

async function handleGenerateFollowUp(followUpId: string, emailId: string, forceGeneration: boolean = false) {
  // Add email to generating set
  generatingEmailIds.value.add(emailId)
  
  try {
    const result = await generateFollowUp(emailId, undefined, forceGeneration)
    successMessage.value = forceGeneration ? 'Follow-up email generated successfully (forced)' : 'Follow-up email generated successfully'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    
    // Notify other components that follow-ups were created
    console.log('[FollowUpsView] Emitting follow-up created event (count: 1)')
    emailEvents.followUpsCreated(1)
    
    await fetchFollowUps()
  } catch (err: any) {
    console.error('Error generating follow-up:', err)
    
    // Check if this is a "too recent" error that can be forced
    if (err.type === 'too_recent' && err.can_force) {
      tooRecentError.value = err
      showTooRecentDialog.value = true
    }
    // Otherwise, the error will be shown in the general error message
  } finally {
    // Remove email from generating set
    generatingEmailIds.value.delete(emailId)
  }
}

async function handleForceGeneration() {
  if (!tooRecentError.value) return
  
  const emailId = tooRecentError.value.email_id
  showTooRecentDialog.value = false
  tooRecentError.value = null
  
  // Call the main function with force flag
  await handleGenerateFollowUp(null, emailId, true)
}

function cancelForceGeneration() {
  showTooRecentDialog.value = false
  tooRecentError.value = null
}

async function handleSkipFollowUp(emailId: string) {
  if (!confirm('Are you sure you want to skip this follow-up?')) return
  
  try {
    await skipFollowUp(emailId)
    successMessage.value = 'Follow-up skipped successfully'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    await fetchFollowUps()
  } catch (err) {
    console.error('Error skipping follow-up:', err)
  }
}

function showRescheduleModal(followUp: any) {
  selectedFollowUp.value = followUp
  // Set default date to tomorrow
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  rescheduledDate.value = tomorrow.toISOString().split('T')[0]
  showReschedule.value = true
}


async function handleRescheduleFollowUp() {
  if (!selectedFollowUp.value || !rescheduledDate.value) return
  
  try {
    const emailId = selectedFollowUp.value.email_id || selectedFollowUp.value.id
    await rescheduleFollowUp(emailId, new Date(rescheduledDate.value).toISOString())
    successMessage.value = 'Follow-up rescheduled successfully'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    showReschedule.value = false
    selectedFollowUp.value = null
    rescheduledDate.value = ''
    await fetchFollowUps()
  } catch (err) {
    console.error('Error rescheduling follow-up:', err)
  }
}

// Selection functions
function toggleAllItems(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.checked) {
    selectedItems.value = recommendations.value.map(rec => rec.email_id)
  } else {
    selectedItems.value = []
  }
  lastSelectedIndex.value = null
}

function handleRowSelection(event: MouseEvent, index: number) {
  // Prevent text selection and context menu during multiselect operations
  event.preventDefault()
  event.stopPropagation()
  
  // Clear any text selection that might have occurred
  if (window.getSelection) {
    window.getSelection()?.removeAllRanges()
  }
  
  const emailId = recommendations.value[index].email_id
  
  if (event.shiftKey && lastSelectedIndex.value !== null) {
    // Handle shift-click range selection
    const start = Math.min(lastSelectedIndex.value, index)
    const end = Math.max(lastSelectedIndex.value, index)
    
    console.log(`Shift+click: selecting range from ${start} to ${end}`)
    
    // For shift+click, we always select all items in the range
    const itemsInRange = []
    for (let i = start; i <= end; i++) {
      itemsInRange.push(recommendations.value[i].email_id)
    }
    
    // Add all items in range to selection if not already selected
    itemsInRange.forEach(rangeEmailId => {
      if (!selectedItems.value.includes(rangeEmailId)) {
        selectedItems.value.push(rangeEmailId)
      }
    })
    
    console.log(`Selected items count: ${selectedItems.value.length}`)
  } else {
    // Handle normal click - toggle single item
    if (!selectedItems.value) {
      selectedItems.value = []
    }

    const idx = selectedItems.value.indexOf(emailId)
    if (idx === -1) {
      selectedItems.value.push(emailId)
      console.log(`Added item ${index} to selection`)
    } else {
      selectedItems.value.splice(idx, 1)
      console.log(`Removed item ${index} from selection`)
    }
  }
  
  lastSelectedIndex.value = index
}

function clearSelection() {
  selectedItems.value = []
  lastSelectedIndex.value = null
}

async function handleGenerateSelectedFollowUps() {
  if (!selectedItems.value.length) return
  
  if (!confirm(`Are you sure you want to generate follow-ups for ${selectedItems.value.length} selected items?`)) return
  
  try {
    const result = await processBulkRecommendations({
      email_ids: selectedItems.value,
      action: 'generate',
      max_emails: selectedItems.value.length
    })
    
    successMessage.value = result.message
    setTimeout(() => {
      successMessage.value = ''
    }, 5000)
    
    // Notify other components that follow-ups were created
    console.log(`[FollowUpsView] Emitting bulk follow-up created event (count: ${selectedItems.value.length})`)
    emailEvents.followUpsCreated(selectedItems.value.length)
    
    // Clear selection and refresh
    clearSelection()
    await fetchFollowUps()
  } catch (err) {
    console.error('Error generating selected follow-ups:', err)
  }
}

async function handleSkipSelectedFollowUps() {
  if (!selectedItems.value.length) return
  
  if (!confirm(`Are you sure you want to skip follow-ups for ${selectedItems.value.length} selected items?`)) return
  
  try {
    const result = await processBulkRecommendations({
      email_ids: selectedItems.value,
      action: 'skip',
      max_emails: selectedItems.value.length
    })
    
    successMessage.value = result.message
    setTimeout(() => {
      successMessage.value = ''
    }, 5000)
    
    // Clear selection and refresh
    clearSelection()
    await fetchFollowUps()
  } catch (err) {
    console.error('Error skipping selected follow-ups:', err)
  }
}

// Manual follow-up functions
function addRecipient() {
  manualFollowUp.value.recipients.push({ email: '', name: '' })
}

function removeRecipient(index: number) {
  if (manualFollowUp.value.recipients.length > 1) {
    manualFollowUp.value.recipients.splice(index, 1)
  }
}

function openManualFollowUpModal() {
  // Pre-populate recipients from selected rows if any are selected
  if (selectedItems.value.length > 0) {
    const selectedRecommendations = recommendations.value.filter(rec => 
      selectedItems.value.includes(rec.email_id)
    )
    
    // Create recipients array from selected items
    const recipients = selectedRecommendations.map(rec => ({
      email: rec.recipient_email,
      name: rec.recipient_name || rec.recipient_email
    }))
    
    // Get the most common institution from selected items
    const institutionCounts = selectedRecommendations.reduce((counts, rec) => {
      if (rec.institution_id) {
        counts[rec.institution_id] = (counts[rec.institution_id] || 0) + 1
      }
      return counts
    }, {})
    
    const mostCommonInstitutionId = Object.keys(institutionCounts).reduce((a, b) => 
      institutionCounts[a] > institutionCounts[b] ? a : b, ''
    )
    
    // Pre-fill the form
    manualFollowUp.value = {
      recipients: recipients,
      subject: '',
      body: '',
      institution_id: mostCommonInstitutionId,
      cc_recipients_text: ''
    }
  } else {
    // Reset form if no items selected
    manualFollowUp.value = {
      recipients: [{ email: '', name: '' }],
      subject: '',
      body: '',
      institution_id: '',
      cc_recipients_text: ''
    }
  }
  
  showManualFollowUpModal.value = true
}

function cancelManualFollowUp() {
  showManualFollowUpModal.value = false
  // Reset form
  manualFollowUp.value = {
    recipients: [{ email: '', name: '' }],
    subject: '',
    body: '',
    institution_id: '',
    cc_recipients_text: ''
  }
}

async function handleCreateManualFollowUp() {
  if (!canCreateManualFollowUp.value) return

  try {
    // Parse CC recipients
    const ccRecipients = manualFollowUp.value.cc_recipients_text
      .split(',')
      .map(email => email.trim())
      .filter(email => email && email.includes('@'))

    // Filter out empty recipients
    const validRecipients = manualFollowUp.value.recipients.filter(r => r.email.trim())

    const data = {
      recipients: validRecipients,
      subject: manualFollowUp.value.subject.trim(),
      body: manualFollowUp.value.body.trim(),
      institution_id: manualFollowUp.value.institution_id || undefined,
      cc_recipients: ccRecipients.length ? ccRecipients : undefined
    }

    const result = await createManualFollowUp(data)
    
    successMessage.value = result.message
    setTimeout(() => {
      successMessage.value = ''
    }, 5000)
    
    // Notify other components that follow-ups were created
    const createdCount = result.emails_created || validRecipients.length
    console.log(`[FollowUpsView] Emitting manual follow-up created event (count: ${createdCount})`)
    emailEvents.followUpsCreated(createdCount)
    
    // Clear selection if items were selected
    if (selectedItems.value.length > 0) {
      clearSelection()
    }
    
    cancelManualFollowUp()
    await fetchFollowUps()
  } catch (err) {
    console.error('Error creating manual follow-up:', err)
  }
}

onMounted(async () => {
  await Promise.all([
    fetchFollowUps(),
    loadInstitutions()
  ])
})
</script> 